import React, { useCallback, useState } from "react";

import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import "mapbox-gl/dist/mapbox-gl.css";
import Map, {
  AttributionControl,
  Layer,
  MapEvent,
  MapStyleDataEvent,
  Source,
} from "react-map-gl";

import { getAreaBaseLayer, getAreaOverlapLayer } from "./layers";
import { useMapStyle } from "./config";
import { CompareMapOverlay } from "./CompareMapOverlay";
import { LocationScore } from "@/types";
import { comparisonOverlapGradient, extractBreaks, getFillColor } from "@/utils";

type CompareMapProps = {
  scores: LocationScore[];
  overlap: LocationScore[];
  gradient?: string[];
};

const CompareMap: React.FC<CompareMapProps> = ({ scores, overlap, gradient }) => {
  const theme = useTheme();
  const mapStyle = useMapStyle(theme.palette.mode);

  const [firstTime, setFirstTime] = useState<boolean>(true);

  const onLoad = useCallback(
    (event: MapEvent) => {
      const map = event.target;
      map && scores.forEach((value) => {
          map.setFeatureState(
            {
              id: value.gid,
              source: "area-source",
              sourceLayer: "postal_area_test",
            },
            { score: value.rate, name: value.name, level: value.level }
          );
        });
      map && overlap.forEach((value) => {
          map.setFeatureState(
            {
              id: value.gid,
              source: "overlap-source",
              sourceLayer: "postal_area_test",
            },
            { score: value.rate, name: value.name, level: value.level }
          );
        });
      map && map.setPaintProperty(
          "area-base",
          "fill-color",
          getFillColor(extractBreaks(scores), gradient ?? theme.palette.gradient)
        );
      map && map.setPaintProperty(
          "area-overlap",
          "fill-color",
          getFillColor(extractBreaks(overlap), comparisonOverlapGradient)
        );
    },
    [scores, overlap, gradient]
  );

  const onStyleData = useCallback((event: MapStyleDataEvent) => {
    setFirstTime(false);
  }, []);

  return (
    <Box
      height={500}
      minHeight={500}
      // minWidth='275px'
      position="relative"
      sx={{
        overflow: "hidden",
        border: 1,
        borderRadius: 1,
        borderColor: theme.palette.divider,
        ".mapbox-improve-map": {
          display: "none",
        },
        ".mapboxgl-ctrl.mapboxgl-ctrl-attrib": {
          backgroundColor: "transparent !important",
        },
        ".mapboxgl-ctrl-attrib-inner": {
          color: theme.palette.text.secondary,
        },
        ".mapboxgl-ctrl-attrib a": {
          color: theme.palette.text.secondary,
        },
      }}
    >
      <Map
        initialViewState={{
          longitude: -4.3,
          latitude: 54.8,
          zoom: 1,
        }}
        projection={{ name: "mercator" }}
        minZoom={4}
        maxBounds={[
          [-30, 36],
          [30, 66],
        ]}
        mapStyle={mapStyle}
        mapboxAccessToken={process.env.REACT_APP_MAPBOX_MAP_TOKEN}
        onLoad={onLoad}
        onStyleData={onStyleData}
        attributionControl={false}
        touchPitch={false}
        dragPan={false}
        dragRotate={false}
        interactive={false}
      >
        <AttributionControl customAttribution=" " />
        <Source
          id="area-source"
          type="vector"
          url="mapbox://mpilarczykstarcount.postal_area_test"
        >
          <Layer beforeId="waterway-label" {...getAreaBaseLayer(gradient ?? theme.palette.gradient)} />
        </Source>
        <Source
          id="overlap-source"
          type="vector"
          url="mapbox://mpilarczykstarcount.postal_area_test"
        >
          <Layer beforeId="waterway-label" {...getAreaOverlapLayer(comparisonOverlapGradient)} />
        </Source>
        <CompareMapOverlay scores={scores} overlap={overlap} gradient={gradient} firstTime={firstTime} />
      </Map>
    </Box>
  );
};

export default CompareMap;
